import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserCheck, UserX, ShieldCheck, Shield, Trash2, Mail, Users as UsersIcon, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ROLE_LABELS } from "@/constants";
import { useAuth } from "@/context/AuthContext";
import PermissionsDialog from "@/components/PermissionsDialog";

const ROLE_COLORS = {
  admin: "bg-[hsl(var(--primary)/0.18)] text-primary border-[hsl(var(--primary)/0.4)]",
  sup: "bg-[hsl(var(--accent)/0.18)] text-[hsl(var(--accent))] border-[hsl(var(--accent)/0.4)]",
  usuario: "bg-muted/40 text-muted-foreground border-border",
};
const STATUS_COLORS = {
  active: "bg-[hsl(var(--status-completed)/0.18)] text-[hsl(var(--status-completed))] border-[hsl(var(--status-completed)/0.4)]",
  pending: "bg-[hsl(var(--status-pending)/0.18)] text-[hsl(var(--status-pending))] border-[hsl(var(--status-pending)/0.4)]",
  disabled: "bg-[hsl(var(--status-cancelled)/0.18)] text-[hsl(var(--status-cancelled))] border-[hsl(var(--status-cancelled)/0.4)]",
};

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const [permsTarget, setPermsTarget] = useState(null);
  const [contactTarget, setContactTarget] = useState(null); // { id, email, equipo }
  const { data: users = [], refetch } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const approve = async (id) => {
    try {
      await api.post(`/users/${id}/approve`);
      toast.success("Usuario aprobado");
      refetch();
    } catch (err) {
      toast.error("Error al aprobar");
    }
  };

  const updateRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Rol actualizado");
      refetch();
    } catch (err) {
      toast.error("Error al actualizar rol");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/users/${id}`, { status });
      toast.success("Estatus actualizado");
      refetch();
    } catch (err) {
      toast.error("Error");
    }
  };

  const updateEquipo = async (id, equipo) => {
    try {
      await api.patch(`/users/${id}`, { equipo });
      toast.success("Equipo actualizado");
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar equipo");
    }
  };

  const saveContact = async () => {
    if (!contactTarget) return;
    try {
      await api.patch(`/users/${contactTarget.id}`, {
        email: (contactTarget.email || "").trim(),
        equipo: contactTarget.equipo,
      });
      toast.success("Datos de contacto guardados");
      refetch();
      setContactTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar contacto");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      refetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const pending = users.filter((u) => u.status === "pending");
  const rest = users.filter((u) => u.status !== "pending");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Usuarios</h1>
        <p className="text-sm text-muted-foreground">Aprueba registros, asigna roles y gestiona el acceso.</p>
      </div>

      {pending.length > 0 && (
        <Card className="bg-card border-border border-[hsl(var(--status-pending)/0.4)]">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-[hsl(var(--status-pending))]" /> Pendientes de aprobación ({pending.length})
            </h3>
            <div className="space-y-2">
              {pending.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/20" data-testid={`user-pending-${u.username}`}>
                  <div>
                    <div className="text-sm font-medium">{u.full_name || u.username}</div>
                    <div className="text-xs text-muted-foreground">@{u.username}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select defaultValue="usuario" onValueChange={(v) => updateRole(u.id, v)}>
                      <SelectTrigger className="w-32 h-9" data-testid={`user-role-pending-${u.username}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="usuario">Usuario</SelectItem>
                        <SelectItem value="sup">Supervisor</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => approve(u.id)} data-testid={`user-approve-${u.username}`}><UserCheck className="h-4 w-4 mr-1" /> Aprobar</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="lg:hidden text-[10px] text-muted-foreground uppercase tracking-wider p-3 pb-0 flex items-center gap-1">
            <span aria-hidden>←</span>
            <span>Desliza para ver más columnas</span>
            <span aria-hidden>→</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-3">Usuario</th>
                  <th className="py-2 px-3">Nombre</th>
                  <th className="py-2 px-3">Rol</th>
                  <th className="py-2 px-3">Equipo / Correo</th>
                  <th className="py-2 px-3">Estatus</th>
                  <th className="py-2 px-3">Permisos</th>
                  <th className="py-2 px-3">Creado</th>
                  <th className="py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody data-testid="users-table-body">
                {rest.map((u) => (
                  <tr key={u.id} className="border-b border-border/60 hover:bg-muted/30">
                    <td className="py-2 px-3 font-mono">@{u.username}</td>
                    <td className="py-2 px-3">{u.full_name || "-"}</td>
                    <td className="py-2 px-3">
                      <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                        <SelectTrigger className="w-36 h-10" data-testid={`user-role-${u.username}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usuario">Usuario</SelectItem>
                          <SelectItem value="sup">Supervisor</SelectItem>
                          <SelectItem value="admin">Administrador</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-col gap-1">
                        <Select value={u.equipo || "Otro"} onValueChange={(v) => updateEquipo(u.id, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs" data-testid={`user-equipo-${u.username}`}>
                            <UsersIcon className="h-3.5 w-3.5 text-muted-foreground mr-1" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Maquila">Maquila</SelectItem>
                            <SelectItem value="ESI">ESI</SelectItem>
                            <SelectItem value="CEDIC">CEDIC</SelectItem>
                            <SelectItem value="Compras">Compras</SelectItem>
                            <SelectItem value="Otro">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 justify-start px-1 gap-1 text-[11px] font-mono text-muted-foreground hover:text-foreground max-w-[180px]"
                          onClick={() => setContactTarget({ id: u.id, username: u.username, email: u.email || "", equipo: u.equipo || "Otro" })}
                          data-testid={`user-email-${u.username}`}
                          title={u.email ? "Editar correo" : "Agregar correo para notificaciones"}
                        >
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate">{u.email || "Sin correo"}</span>
                        </Button>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <Select value={u.status} onValueChange={(v) => updateStatus(u.id, v)}>
                        <SelectTrigger className="w-32 h-10" data-testid={`user-status-${u.username}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Activo</SelectItem>
                          <SelectItem value="disabled">Deshabilitado</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      {u.role === "admin" ? (
                        <Badge variant="outline" className="text-[10px] gap-1"><Shield className="h-3 w-3" />Todos</Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-10 gap-1.5"
                          onClick={() => setPermsTarget(u)}
                          data-testid={`user-perms-${u.username}`}
                        >
                          <Shield className="h-3.5 w-3.5 text-primary" />
                          <span className="font-mono text-xs">{(u.permissions || []).length}</span>
                          <span className="text-xs text-muted-foreground">módulos</span>
                        </Button>
                      )}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground font-mono text-xs">{(u.created_at || "").substring(0, 10)}</td>
                    <td className="py-2 px-3">
                      {currentUser?.id !== u.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-10 w-10 text-destructive" data-testid={`user-delete-${u.username}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar @{u.username}?</AlertDialogTitle>
                              <AlertDialogDescription>El usuario no podrá volver a iniciar sesión.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(u.id)} data-testid={`user-delete-confirm-${u.username}`}>Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </td>
                  </tr>
                ))}
                {rest.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">Sin usuarios activos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <PermissionsDialog
        user={permsTarget}
        open={!!permsTarget}
        onOpenChange={(o) => { if (!o) setPermsTarget(null); }}
        onSaved={refetch}
      />

      {/* Contact (email + equipo) dialog */}
      <Dialog open={!!contactTarget} onOpenChange={(o) => { if (!o) setContactTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Contacto de @{contactTarget?.username}</DialogTitle>
            <DialogDescription>
              El correo se usa para enviar notificaciones automáticas por eventos. El equipo determina qué notificaciones recibe (además de las de admin, si aplica).
            </DialogDescription>
          </DialogHeader>
          {contactTarget && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Correo electrónico</label>
                <Input
                  type="email"
                  placeholder="usuario@empresa.com"
                  value={contactTarget.email}
                  onChange={(e) => setContactTarget({ ...contactTarget, email: e.target.value })}
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Equipo</label>
                <Select value={contactTarget.equipo} onValueChange={(v) => setContactTarget({ ...contactTarget, equipo: v })}>
                  <SelectTrigger data-testid="contact-equipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Maquila">Maquila — recibe notificaciones de OT nueva, etc.</SelectItem>
                    <SelectItem value="ESI">ESI — recibe notificaciones de evidencias / clasificaciones</SelectItem>
                    <SelectItem value="CEDIC">CEDIC — configurable por evento en Notificaciones</SelectItem>
                    <SelectItem value="Compras">Compras — configurable por evento en Notificaciones</SelectItem>
                    <SelectItem value="Otro">Otro — no recibe notificaciones por equipo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setContactTarget(null)}>Cancelar</Button>
            <Button onClick={saveContact} data-testid="contact-save-button"><Save className="h-4 w-4 mr-1" />Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
